import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Users, Search, Trash2, Pencil } from 'lucide-react';
import { RoleBadge } from './shared';

export const AdminUsersTable = ({
  users, currentUserId, isSuper,
  search, onSearch,
  selectedIds, onToggleSelect, onToggleSelectAll,
  onOpenEdit, onOpenDelete, onOpenBulk,
}) => (
  <Card className="bg-zinc-950/60 border-white/5">
    <CardHeader className="flex flex-row items-center justify-between gap-4 flex-wrap">
      <CardTitle className="font-heading text-lg flex items-center gap-2">
        <Users className="w-4 h-4 text-muted-foreground" />
        All Users
        <span className="text-sm text-muted-foreground font-normal">({users.length})</span>
        {selectedIds.length > 0 && (
          <span className="text-xs text-primary font-normal ml-2">
            · {selectedIds.length} selected
          </span>
        )}
      </CardTitle>
      <div className="flex items-center gap-2 flex-wrap">
        {selectedIds.length > 0 && (
          <Button
            onClick={onOpenBulk}
            className="bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 border border-amber-500/30"
            data-testid="admin-bulk-action-button"
          >
            Bulk actions ({selectedIds.length})
          </Button>
        )}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by email or name…"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            className="pl-9 w-72 bg-zinc-900/60 border-white/10"
            data-testid="admin-search-input"
          />
        </div>
      </div>
    </CardHeader>
    <CardContent className="px-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-muted-foreground border-y border-white/5">
              <th className="px-6 py-3 w-8">
                <Checkbox
                  checked={users.length > 0 && users.every(u => selectedIds.includes(u.id))}
                  onCheckedChange={() => onToggleSelectAll(users)}
                  data-testid="admin-select-all"
                  aria-label="Select all visible users"
                />
              </th>
              <th className="text-left font-medium px-3 py-3">User</th>
              <th className="text-left font-medium px-3 py-3">Role</th>
              <th className="text-left font-medium px-3 py-3">Plan</th>
              <th className="text-left font-medium px-3 py-3">Credits</th>
              <th className="text-left font-medium px-3 py-3">Videos</th>
              <th className="text-left font-medium px-3 py-3">Gens</th>
              <th className="text-right font-medium px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const total = (u.monthly_credits || 0) + (u.purchased_credits || 0);
              const isMe = u.id === currentUserId;
              return (
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`admin-user-row-${u.email}`}>
                  <td className="px-6 py-3">
                    <Checkbox
                      checked={selectedIds.includes(u.id)}
                      onCheckedChange={() => onToggleSelect(u.id)}
                      disabled={isMe}
                      data-testid={`admin-select-${u.email}`}
                      aria-label={`Select ${u.email}`}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      {u.avatar_url ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${u.avatar_url}`} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold">
                          {u.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-xs text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3"><RoleBadge role={u.role || 'user'} /></td>
                  <td className="px-3 py-3 capitalize text-muted-foreground">{u.plan || 'free'}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-medium">{total}</span>
                      <span className="text-xs text-muted-foreground">({u.monthly_credits || 0}+{u.purchased_credits || 0})</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-muted-foreground">{u.videos_count}</td>
                  <td className="px-3 py-3 text-muted-foreground">{u.generations_count}</td>
                  <td className="px-6 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onOpenEdit(u)}
                        data-testid={`admin-edit-${u.email}`}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1.5" />
                        Edit
                      </Button>
                      {isSuper && !isMe && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => onOpenDelete(u)}
                          data-testid={`admin-delete-${u.email}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {users.length === 0 && (
          <p className="text-center py-12 text-sm text-muted-foreground">No users found.</p>
        )}
      </div>
    </CardContent>
  </Card>
);
